// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.
//
// D-POT-5 §2.5 — the dashboard's confirm pop-ups (asp-pot spec §3.2): Delete / Delete selected,
// Void (reason), Revoke envelope (reason), Duplicate (→ claim → open), Reassign… / Share… (nurse
// picker + reason). Same wording and endpoints as POT classic; every write is a query mutation that
// refetches the dashboard on success and shows the server message on failure (E-07).
import { useEffect, useMemo, useState } from 'react'
import Box from '@mui/material/Box'
import { claimCareRxPot } from '../../api/client'
import SearchableSelect, { type SearchableOption } from '../../components/common/SearchableSelect'
import ConfirmDialog from '../../components/ui/ConfirmDialog'
import { useToast } from '../../components/ui/Toast'
import { useCareRxNurseRoster, useDuplicatePot, useReassignPot, useRevokePotEnvelope, useSharePot, useVoidPotVisit } from '../../hooks/queries/carerxPot'
import { apiErrorMessage } from './carerxPotRequest'
import { assignableNurseOptions, type PotRow } from './carerxPotDashboardLogic'

export type PotDlg =
  | { kind: 'delete'; row: PotRow }
  | { kind: 'bulkDelete'; ids: string[] }
  | { kind: 'void'; row: PotRow }
  | { kind: 'revoke'; row: PotRow }
  | { kind: 'duplicate'; row: PotRow }
  | { kind: 'reassign'; row: PotRow }
  | { kind: 'share'; row: PotRow }
  | null

export default function CareRxPotRowDialogs({ dlg, onClose, onBulkDeleted, onOpenForm, pharmacyName }: {
  dlg: PotDlg; onClose: () => void; onBulkDeleted: () => void; onOpenForm: (visitId: string) => void; pharmacyName: string
}) {
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const voidVisit = useVoidPotVisit()
  const reassign = useReassignPot()
  const share = useSharePot()
  const duplicate = useDuplicatePot()
  const revoke = useRevokePotEnvelope()
  useEffect(() => { setErr(null); setBusy(false); setPicked('') }, [dlg])

  // Reassign… / Share… are pharmacy_staff / admin endpoints; the roster loads the first time either opens.
  // The picker value is the nurse's LOGIN id (NurseRow.nurse_id), which both endpoints take — only nurses with
  // an active login can be picked (assignableNurseOptions).
  const transferRow = dlg?.kind === 'reassign' || dlg?.kind === 'share' ? dlg.row : null
  const roster = useCareRxNurseRoster(!!transferRow)
  const [picked, setPicked] = useState('')
  // a visit nobody holds is ASSIGNED (no reason needed); one a nurse holds is REASSIGNED
  const firstAssign = dlg?.kind === 'reassign' && !dlg.row.nurse_id
  const nurseOptions = useMemo<SearchableOption[]>(() => assignableNurseOptions(roster.data ?? [], transferRow?.nurse_id), [roster.data, transferRow])
  const rosterErr = roster.isError ? apiErrorMessage(roster.error, 'Could not load the nurse roster') : null

  const run = async (fn: () => Promise<unknown>, okToast?: string) => {
    setBusy(true); setErr(null)
    try { await fn(); if (okToast) toast.show(okToast, 'success'); onClose() }
    catch (e) { setErr(apiErrorMessage(e)) }
    finally { setBusy(false) }
  }
  const bulkDelete = async (ids: string[]) => {
    setBusy(true)
    const failed: string[] = []
    for (const id of ids) {
      try { await voidVisit.mutateAsync({ visitId: id, reason: 'Draft deleted from the Dashboard (bulk)' }) }
      catch (e) { failed.push(apiErrorMessage(e)) }
    }
    const ok = ids.length - failed.length
    toast.show(failed.length === 0 ? `Deleted ${ids.length} drafts` : `Deleted ${ok} of ${ids.length} drafts. ${failed.length} failed: ${failed[0]}`, failed.length === 0 ? 'success' : 'error')
    setBusy(false); onBulkDeleted(); onClose()
  }
  const picker = (
    <SearchableSelect value={picked} onChange={setPicked} options={nurseOptions} ariaLabel="Nurse"
      placeholder={roster.isPending ? 'Loading nurses…' : 'Select a nurse...'} emptyText="No active nurse logins" />
  )

  return (
    <>
      <ConfirmDialog open={dlg?.kind === 'delete'} tone="danger" title="Delete draft" confirmLabel="Delete" busy={busy} error={err}
        body={dlg?.kind === 'delete' ? <>Delete draft for patient <b>{dlg.row.patient_name}</b>? This cannot be undone.</> : null}
        onCancel={onClose} onConfirm={() => { if (dlg?.kind === 'delete') void run(() => voidVisit.mutateAsync({ visitId: dlg.row.visit_id, reason: 'Draft deleted from the Dashboard' }), 'Draft deleted.') }} />

      <ConfirmDialog open={dlg?.kind === 'bulkDelete'} tone="danger" title="Delete drafts" confirmLabel="Delete" busy={busy} error={err}
        body={dlg?.kind === 'bulkDelete' ? <>Delete {dlg.ids.length} drafts?<br /><br />This cannot be undone.</> : null}
        onCancel={onClose} onConfirm={() => { if (dlg?.kind === 'bulkDelete') void bulkDelete(dlg.ids) }} />

      <ConfirmDialog open={dlg?.kind === 'void'} tone="danger" title="Void assessment" confirmLabel="Void assessment" busy={busy} error={err}
        requireReason={{ label: 'Reason (required)', minLength: 5, placeholder: 'e.g. Duplicate of the POT submitted earlier today' }}
        body={dlg?.kind === 'void' ? <>Void <b>{dlg.row.patient_name}</b>'s submitted assessment? It will be hidden from lists, queues and reports and the action is recorded in the security log. This cannot be undone.</> : null}
        onCancel={onClose} onConfirm={({ reason }) => { if (dlg?.kind === 'void') void run(() => voidVisit.mutateAsync({ visitId: dlg.row.visit_id, reason }), 'Assessment voided.') }} />

      <ConfirmDialog open={dlg?.kind === 'revoke'} tone="danger" title="Void this POT envelope?" confirmLabel="Revoke" busy={busy} error={err}
        requireReason={{ label: 'Reason', minLength: 1, placeholder: `Voided by ${pharmacyName}` }}
        body="Provide a reason. The pending signature request is cancelled so the POT can be re-sent."
        onCancel={onClose} onConfirm={({ reason }) => {
          if (dlg?.kind !== 'revoke') return
          const env = dlg.row.envelope_id
          if (!env) { setErr('This assessment has no envelope to revoke.'); return }
          void run(() => revoke.mutateAsync({ envelopeId: env, reason }), 'Signature request revoked.')
        }} />

      <ConfirmDialog open={dlg?.kind === 'duplicate'} title="Duplicate assessment" confirmLabel="Duplicate" busy={busy} error={err}
        body={dlg?.kind === 'duplicate' ? <>Create a new draft from {dlg.row.patient_name}'s completed assessment? The original stays read-only and a new editable draft will be created.</> : null}
        onCancel={onClose} onConfirm={() => {
          if (dlg?.kind !== 'duplicate') return
          void run(async () => {
            const d = await duplicate.mutateAsync(dlg.row.visit_id)
            try { await claimCareRxPot(d.visit_id) } catch { /* not claimable by this account (e.g. staff) — the draft still opens */ }
            onOpenForm(d.visit_id)
          })
        }} />

      <ConfirmDialog open={dlg?.kind === 'reassign'} title={firstAssign ? 'Assign nurse' : 'Reassign assessment'} confirmLabel={firstAssign ? 'Assign' : 'Reassign'} busy={busy} error={err ?? rosterErr} confirmDisabled={!picked}
        requireReason={firstAssign ? undefined : { label: 'Reason (required)', minLength: 5, placeholder: 'e.g. Nurse out sick — covered by the on-call RN' }}
        body={dlg?.kind === 'reassign' ? <>
          <Box sx={{ fontSize: 13, mb: 1.25 }}>{firstAssign
            ? <>Assign <b>{dlg.row.patient_name}</b>'s POT to a nurse. The assignment is recorded.</>
            : <>Move <b>{dlg.row.patient_name}</b>'s open assessment to another nurse. The current nurse loses access; the transfer and your reason are recorded.</>}</Box>
          {picker}
        </> : null}
        onCancel={onClose} onConfirm={({ reason }) => { if (dlg?.kind === 'reassign' && picked) void run(() => reassign.mutateAsync({ visitId: dlg.row.visit_id, nurseId: picked, reason }), firstAssign ? 'Nurse assigned.' : 'Assessment reassigned.') }} />

      <ConfirmDialog open={dlg?.kind === 'share'} title="Share assessment" confirmLabel="Share" busy={busy} error={err ?? rosterErr} confirmDisabled={!picked}
        requireReason={{ label: 'Reason (required)', minLength: 5, placeholder: 'e.g. Co-visit on 10/21 — supervising RN' }}
        body={dlg?.kind === 'share' ? <>
          <Box sx={{ fontSize: 13, mb: 1.25 }}>Give another nurse read access to <b>{dlg.row.patient_name}</b>'s assessment (co-visit / supervision). The owner keeps it; the grant and your reason are recorded.</Box>
          {picker}
        </> : null}
        onCancel={onClose} onConfirm={({ reason }) => { if (dlg?.kind === 'share' && picked) void run(() => share.mutateAsync({ visitId: dlg.row.visit_id, accountId: picked, reason }), 'Assessment shared.') }} />
    </>
  )
}
